import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Skeleton,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { User, ProfileForm } from '@/types';
import { PhotoUpload } from '@/components/common/PhotoUpload';
import { ProfileCompletionIndicator } from '@/components/common/ProfileCompletionIndicator';
import { ProfileService } from '@/utils/profile-service';
import { apiClient } from '@/utils/api-client';

interface ProfileFormProps {
  user: User;
  onProfileUpdate: (updatedUser: User) => void;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters'),
  bio: Yup.string()
    .max(500, 'Bio must be less than 500 characters'),
});

export const ProfileFormComponent: React.FC<ProfileFormProps> = ({
  user,
  onProfileUpdate,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(user.photoUrl);

  const completionStatus = ProfileService.calculateProfileCompletion({
    ...user,
    photoUrl,
  });

  const formik = useFormik<ProfileForm>({
    enableReinitialize: true,
    initialValues: {
      name: user.name || '',
      bio: user.bio || '',
      photoUrl: user.photoUrl,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        // Split the full name into given_name and family_name
        const nameParts = values.name.trim().split(' ');
        const given_name = nameParts[0] || '';
        const family_name = nameParts.slice(1).join(' ') || '';

        const updatedProfile = await ProfileService.updateProfile({
          ...values,
          given_name,
          family_name,
          photoUrl,
        });
        
        onProfileUpdate(updatedProfile);
        setSuccess('Profile updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update profile');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handlePhotoUpload = async (file: File): Promise<string> => {
    try {
      const { uploadUrl, photoUrl: newPhotoUrl, uploadId } = await ProfileService.getPhotoUploadUrl(
        file.name,
        file.type,
        file.size
      );
      
      await ProfileService.uploadPhoto(uploadUrl, file);
      
      // Confirm the upload
      await apiClient.post('/profile/confirm-upload', {
        uploadId,
        photoUrl: newPhotoUrl,
      });
      
      return newPhotoUrl;
    } catch (error) {
      throw new Error('Failed to upload photo');
    }
  };

  const handlePhotoChange = async (newPhotoUrl: string | null) => {
    // If removing photo (newPhotoUrl is null), call delete API
    if (newPhotoUrl === null && photoUrl) {
      try {
        await ProfileService.deletePhoto();
        setPhotoUrl(undefined);
      } catch (error) {
        console.error('Failed to delete profile photo:', error);
        // You might want to show an error message to the user here
      }
    } else {
      setPhotoUrl(newPhotoUrl || undefined);
    }
  };

  // Update photo when user prop changes
  useEffect(() => {
    setPhotoUrl(user.photoUrl);
  }, [user]);

  return (
    <Box>
      {/* Profile Completion Indicator */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <ProfileCompletionIndicator 
          completionStatus={completionStatus}
          showDetails={!completionStatus.isComplete}
        />
      </Paper>

      {/* Profile Form */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Profile Information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Keep your profile up to date to make the best impression on potential guests.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={4}>
            {/* Photo Upload Section */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Profile Photo
              </Typography>
              <PhotoUpload
                currentPhotoUrl={photoUrl}
                onPhotoChange={handlePhotoChange}
                onUpload={handlePhotoUpload}
                disabled={isLoading}
              />
            </Grid>

            {/* Form Fields Section */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="name"
                    name="name"
                    label="Full Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    disabled={isLoading}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="bio"
                    name="bio"
                    label="Bio"
                    multiline
                    rows={4}
                    value={formik.values.bio}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.bio && Boolean(formik.errors.bio)}
                    helperText={
                      (formik.touched.bio && formik.errors.bio) ||
                      `${formik.values.bio.length}/500 characters`
                    }
                    disabled={isLoading}
                    placeholder="Tell potential guests about yourself, your hosting experience, and what makes your property special..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={user.email}
                    disabled
                    helperText="Email cannot be changed. Contact support if you need to update your email address."
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isLoading || !formik.isValid}
                  sx={{ minWidth: 120 }}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    formik.resetForm();
                    setPhotoUrl(user.photoUrl);
                    setError(null);
                    setSuccess(null);
                  }}
                  disabled={isLoading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default ProfileFormComponent;